/* Blair TLV — header glass overlay v6 (2026-05-30)
   Changes from v5:
   - Marquee (announcement bar) moved OUT of the fixed wrapper into the top
     of <body>. It now sits in normal flow above the header and scrolls away
     with the page (conventional layout: marquee → header → content).
   - Wrapper holds only the header. Switched from fixed to sticky so the
     header is below the marquee at rest, then sticks to top: 0 after the
     marquee scrolls out.
   - Hide-on-scroll-down / show-on-scroll-up still works via transform.
*/
(function () {
  if (window.Shopify && window.Shopify.designMode) return;

  var THRESHOLD_OPACITY = 24;
  var THRESHOLD_HIDE = 160;
  var lastY = 0;
  var ticking = false;
  var scrolledFlag = null;
  var hiddenFlag = null;
  var wrapper = null;

  function setupLayout() {
    var sections = document.querySelectorAll('.shopify-section-group-header-group');
    if (!sections.length) return null;
    var existing = document.querySelector('.blair-header-fixed');
    if (existing) return existing;

    var headerSection = null;
    var announcementSection = null;
    sections.forEach(function (s) {
      if (s.classList.contains('section-header')) headerSection = s;
      else announcementSection = s;
    });

    var anchor = sections[0];
    var parent = anchor.parentNode;

    // Build the sticky wrapper at the original header position, containing only the header
    var w = document.createElement('div');
    w.className = 'blair-header-fixed';
    parent.insertBefore(w, anchor.nextSibling || null);
    if (headerSection) w.appendChild(headerSection);

    // Place the announcement bar in normal flow immediately AFTER the header wrapper
    if (announcementSection) {
      w.parentNode.insertBefore(announcementSection, w.nextSibling);
    }

    return w;
  }

  // Blair (Claude 2026-07-29, perf): only WRITE the variable when the height actually changed.
  // --blair-header-h lives on <html> and the deck sizes every panel from it, so re-setting it
  // invalidates style for the whole document — even when re-setting it to the identical value.
  // This used to fire on every resize plus two timers, i.e. several whole-document recalcs per page
  // load for a number that almost never changes. Traced at ~133ms of forced layout per page.
  var _lastH = -1;
  function measureHeader() {
    if (!wrapper) return;
    var h = wrapper.getBoundingClientRect().height;
    if (h > 0 && h !== _lastH) {
      _lastH = h;
      document.documentElement.style.setProperty('--blair-header-h', h + 'px');
    }
  }

  // Blair (blairtlv1 2026-07-16, perf): cache the two menu elements once and read their state each
  // scroll frame (classList/hasAttribute) instead of two document.querySelector DOM traversals per
  // frame. Same result; much cheaper on the scroll hot path. `false` = looked up and absent.
  var _shEl, _drEl;
  function menuIsOpen() {
    if (_shEl === undefined) _shEl = document.querySelector('.section-header') || false;
    if (_drEl === undefined) _drEl = document.getElementById('Details-menu-drawer-container') || false;
    return (_shEl && _shEl.classList.contains('menu-open')) || (_drEl && _drEl.hasAttribute('open'));
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var y = window.scrollY || window.pageYOffset || 0;
      // Founder 2026-06-24: never hide the header while the nav drawer is open.
      var menuOpen = menuIsOpen();
      if (menuOpen) {
        if (wrapper && hiddenFlag !== false) { wrapper.classList.remove('is-hidden'); hiddenFlag = false; }
        lastY = y; ticking = false; return;
      }
      var past = y > THRESHOLD_OPACITY;

      if (past !== scrolledFlag) {
        document.body.classList.toggle('scrolled-past-header', past);
        scrolledFlag = past;
      }

      if (wrapper && y > THRESHOLD_HIDE) {
        var goingDown = y > lastY + 2;
        var goingUp = y < lastY - 2;
        if (goingDown && hiddenFlag !== true) {
          wrapper.classList.add('is-hidden');
          hiddenFlag = true;
        } else if (goingUp && hiddenFlag !== false) {
          wrapper.classList.remove('is-hidden');
          hiddenFlag = false;
        }
      } else if (wrapper && hiddenFlag !== false) {
        wrapper.classList.remove('is-hidden');
        hiddenFlag = false;
      }

      lastY = y;
      ticking = false;
    });
  }

  function init() {
    wrapper = setupLayout();
    measureHeader();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Blair (Claude 2026-07-29, perf): watch the header's box instead of polling it. The two timers
    // existed to catch the header settling after fonts/logo load; a ResizeObserver catches exactly
    // that, but only when the box REALLY changes, and its callback runs in a phase where the layout
    // is already clean — so reading getBoundingClientRect there does not force an extra layout the
    // way the timer version did. Same measurement, same variable, far fewer whole-document recalcs.
    var polled = function () {
      window.addEventListener('resize', measureHeader, { passive: true });
      setTimeout(measureHeader, 400);
      setTimeout(measureHeader, 1200);
    };
    if ('ResizeObserver' in window) {
      try {
        new ResizeObserver(measureHeader).observe(wrapper);
      } catch (e) {
        polled();
      }
    } else {
      polled();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Instagram section heading bidi fix — the Spotlight Instafeed app renders
     its heading with leading !!! (source: "!!!עקבו..."). In RTL bidi this
     puts the !!! at the visual RIGHT, but in Hebrew convention exclamation
     marks belong at the END of the sentence (visual LEFT in RTL). Move them
     to the end of the string so bidi renders them on the left correctly. */
  function fixInstafeedHeadingBidi() {
    if (document.documentElement.dir !== 'rtl') return;
    var feed = document.getElementById('insta-feed');
    if (!feed) return;
    var heading = feed.parentElement && feed.parentElement.querySelector('h1, h2, h3, h4');
    if (!heading) return;
    var text = heading.textContent;
    if (!text) return;
    // Match leading run of !? marks, move to end
    var m = text.match(/^([!?]+)\s*(.+?)\s*$/);
    if (m) {
      heading.textContent = m[2] + m[1];
    }
  }

  // Blair (Claude 2026-07-29, perf): this used to wake up every 500ms for 10 seconds, on EVERY page of
  // the store, running a four-selector document-wide querySelector each time — and it kept going all 20
  // rounds even after the fix had already been applied. Three things are wrong with that: the feed only
  // exists on the pages that have the Instagram section, the poll never stopped early, and the repeated
  // wake-ups keep the main thread from settling (this script showed up as two ~215ms long tasks on the
  // collection page). Now: bail immediately on pages with no Instagram section at all, stop the moment
  // the fix lands, and keep the same 10s worst-case window for the app to inject its markup late.
  function pollInstafeed() {
    if (document.documentElement.dir !== 'rtl') return;
    var SEL = '#insta-feed h1, #insta-feed ~ * h2, #insta-feed h2, #shopify-section-template--18072529305828__17459611929b25bfa3 h2';
    function tryFix() {
      if (!document.querySelector(SEL)) return false;
      fixInstafeedHeadingBidi();
      return true;
    }
    if (tryFix()) return;                              // already in the DOM: done, no timer at all
    // Nothing to fix and no Instagram section on this page (the common case, e.g. every product and
    // collection page) → never start the timer.
    if (!document.getElementById('insta-feed') &&
        !document.querySelector('[id*="17459611929b25bfa3"]')) return;
    var tries = 0;
    var iv = setInterval(function () {
      if (tryFix() || ++tries > 20) clearInterval(iv);  // stop as soon as it works
    }, 500);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pollInstafeed);
  } else {
    pollInstafeed();
  }
})();
